import { useRef } from "react";
import { useTranslation } from "next-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { cn } from "../../../../../lib/utils";

const ASSETS_PATH = "/landings/assets/images/products/hub-talentos";

const SLIDES_CONFIG = [
	{ key: "slide01", src: "alocacao-slide-01.png" },
	{ key: "slide02", src: "alocacao-slide-02.png" },
	{ key: "slide03", src: "alocacao-slide-03.png" },
	{ key: "slide04", src: "alocacao-slide-04.png" },
	{ key: "slide05", src: "alocacao-slide-05.png" },
	{ key: "slide06", src: "alocacao-slide-06.png" },
	{ key: "slide07", src: "alocacao-slide-07.png" },
	{ key: "slide08", src: "alocacao-slide-08.png" },
	{ key: "slide09", src: "alocacao-slide-09.png" },
	{ key: "slide10", src: "alocacao-slide-10.png" },
];

export default function AlocacaoSection() {
	const { t } = useTranslation();
	const swiperRef = useRef(null);

	return (
		<section className="relative overflow-hidden">
			{/* Background image - right side, flipped horizontally */}
			<div className="hidden tablet:block absolute w-[832px] h-[555px] laptopM:w-[888px] laptopM:h-[592px] bottom-0 right-0 pointer-events-none">
				<img
					src={`${ASSETS_PATH}/alocacao-bg.jpg`}
					alt=""
					className="w-full h-full object-cover -scale-x-100"
				/>
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							"linear-gradient(to top, rgb(0, 0, 0) 2.3%, rgba(0, 0, 0, 0) 23%), linear-gradient(to right, rgb(0, 0, 0) 0.5%, rgba(0, 0, 0, 0) 27%)",
					}}
				/>
			</div>

			<div
				className={cn(
					"container-blog",
					"relative flex flex-col items-center gap-8",
					"laptop:flex-row laptop:items-start laptop:gap-[40px]",
					"laptopM:gap-[56px]"
				)}
			>
				{/* Text - left side */}
				<div className="flex flex-col gap-4 max-w-full w-full text-center tablet:text-center laptop:text-left laptop:max-w-[45%] laptopM:max-w-[574px] shrink-0">
					<h2
						className={cn(
							"m-0",
							"text-[24px] leading-[32px]",
							"tablet:text-[24px] tablet:leading-[32px]",
							"laptop:text-[40px] laptop:leading-[48px]",
							"tablet:max-w-full laptop:max-w-[440px]"
						)}
					>
						<span className="font-normal text-vivid-02">
							{t("products.hub-talentos.alocacao.title")}
						</span>
						<br className="hidden tablet:block laptop:hidden" />
						<span className="font-semibold text-vivid-02">
							{" "}
							{t("products.hub-talentos.alocacao.titleHighlight")}
						</span>
					</h2>

					<p
						className={cn(
							"text-white m-0 font-medium",
							"text-base leading-7",
							"tablet:text-sm tablet:leading-[22px]",
							"laptop:text-[20px] laptop:leading-[32px]"
						)}
					>
						{t("products.hub-talentos.alocacao.description")}
					</p>
					<p
						className={cn(
							"text-ds-brand-green-300 font-bold m-0",
							"hidden tablet:block",
							"text-base leading-7",
							"tablet:text-sm tablet:leading-[22px]",
							"laptop:text-[20px] laptop:leading-[32px]"
						)}
					>
						{t("products.hub-talentos.alocacao.highlight")}
					</p>
				</div>

				{/* Image carousel - right side */}
				<div className="flex flex-col items-center gap-4 shrink-0 w-full laptop:w-auto">
					<div
						className={cn(
							"border border-[#e7e7e7] rounded-2xl overflow-hidden",
							"w-full max-w-[650px]",
							"laptop:w-full laptop:max-w-[650px]"
						)}
					>
						<Swiper
							modules={[Navigation, Autoplay, Pagination]}
							autoplay={{ delay: 5000, disableOnInteraction: false }}
							pagination={{ clickable: true, el: ".alocacao-pagination" }}
							spaceBetween={24}
							loop
							onSwiper={(swiper) => {
								swiperRef.current = swiper;
							}}
							className="w-full"
						>
							{SLIDES_CONFIG.map(({ key, src }) => (
								<SwiperSlide key={key}>
									<img
										src={`${ASSETS_PATH}/${src}`}
										alt={t(`products.hub-talentos.alocacao.slides.${key}`)}
										className={cn(
											"object-cover w-full",
											"laptop:h-[392px]"
										)}
									/>
								</SwiperSlide>
							))}
						</Swiper>
					</div>

					{/* Pagination dots - mobile only */}
					<div className="alocacao-pagination tablet:hidden flex items-center justify-center gap-2" />

					{/* Slider arrows - tablet+ */}
					<div className="hidden tablet:flex items-center justify-center gap-4">
						<button
							onClick={() => swiperRef.current?.slidePrev()}
							className="w-6 h-6 rounded-full bg-ds-brand-green-900 flex items-center justify-center border-0 cursor-pointer p-1"
							aria-label="Anterior"
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
							>
								<path
									d="M10 4L6 8L10 12"
									stroke="#449818"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<button
							onClick={() => swiperRef.current?.slideNext()}
							className="w-6 h-6 rounded-full bg-vivid-02 flex items-center justify-center border-0 cursor-pointer p-1"
							aria-label="Próximo"
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
							>
								<path
									d="M6 4L10 8L6 12"
									stroke="#122b08"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>
				</div>

				{/* Highlight text - mobile only (below carousel) */}
				<p
					className={cn(
						"text-ds-brand-green-300 font-medium m-0 text-center",
						"tablet:hidden",
						"text-base leading-6"
					)}
				>
					{t("products.hub-talentos.alocacao.highlight")}
				</p>
			</div>
		</section>
	);
}
